import { Column, CustomMaterialTable } from '@loop/ui'
import { CsvBuilder } from 'filefy'
import { ReactNode } from 'react'
import { useErrorData } from 'src/context/ErrorContext'
import { useSnackData } from 'src/context/SnackContext'
import { ABORT_MESSAGE } from 'src/services/openApiV2/utils/constants'
import { get } from 'src/utils/config/lodashUtils'
import { formatNumber } from 'src/utils/functions'
import processCSV from 'src/utils/workers/functions/processCSV'
import CustomDownloadBtn from './components/CustomDownloadBtn'
import { RenderFunctionType } from './components/ModifyCSVDialog'

export type CustomColumns<T> = Column<T> & { hideInExport?: boolean; exportRender?: RenderFunctionType }

// Note: CustomMaterialTableProps from @loop/ui loses generic type parameter in build
// Defining local type to maintain type safety
type CustomMaterialTableWrapperProps<T extends object> = {
  columns: CustomColumns<T>[]
  data: T[]
  isLoading?: boolean
  onExportModifyCSV?: (data: T[]) => Promise<string | { [key: string]: string | number }[]> | { [key: string]: string | number }[]
  options?: {
    export?: boolean
    loadingExport?: boolean
    csvFileName?: string
    totalPaginatedDataLength?: number
    extraExportColumns?: { title: string; field: string; hideInExport?: boolean; exportRender?: RenderFunctionType }[]
    extraToolbarComponent?: ReactNode | JSX.Element
    [key: string]: any
  }
  [key: string]: any
}

export default function CustomMaterialTableWrapper<T extends object>(props: CustomMaterialTableWrapperProps<T>) {
  const { handleError } = useErrorData()
  const { setDownloadSnack } = useSnackData()

  const onExportModifyCSV = async () => {
    try {
      if (!props.onExportModifyCSV) {
        throw new Error('CSV data is not available')
      }
      setDownloadSnack({ status: 'close' })
      setDownloadSnack({ status: 'start' })
      const csvData = await props.onExportModifyCSV(props.data)
      const concatColumns = props.columns.filter((e) => (e.hideInExport ? false : !e.hidden)).concat(get(props, 'options.extraExportColumns', []))
      const columns = concatColumns.map((item) => {
        return {
          title: item.title,
          field: item.field,
          hidden: item.hideInExport
        }
      })
      processCSV({
        csvData,
        columns,
        onSuccess: (data) => {
          const fileName = get(props, 'options.csvFileName', 'data.csv') as string
          const builder = new CsvBuilder(fileName)

          console.log(concatColumns)

          const exportRenderFns = {}
          concatColumns.forEach((column) => {
            for (let i = 0; i < data.headers.length; i++) {
              if (data.headers[i] === column.title) {
                exportRenderFns[i] = column.exportRender ?? null
              }
            }
          })

          const rows = []
          data.rows.forEach((row) => {
            const newRow = []
            for (let i = 0; i < row.length; i++) {
              if (exportRenderFns[i]) {
                newRow.push(exportRenderFns[i](row[i]))
              } else {
                newRow.push(row[i])
              }
            }
            rows.push(newRow)
          })

          builder.setColumns(data.headers)
          builder.addRows(rows)
          builder.exportFile()
          setDownloadSnack({
            status: 'complete',
            message:
              (get(props, 'options.totalPaginatedDataLength', 0) as number) > 100000
                ? `First ${formatNumber(100000, { maxFractionDigits: 0 })} records are downloaded`
                : undefined
          })
        },
        onError: (msg) => {
          console.log('error: ', msg)
        }
      })
    } catch (error) {
      if (error?.message === ABORT_MESSAGE) {
        return
      }
      setDownloadSnack({ status: 'close' })
      handleError(error.message)
    }
  }

  return (
    <>
      <CustomMaterialTable
        {...props}
        options={{
          ...props.options,
          export: props.onExportModifyCSV ? false : props.options?.export,
          extraToolbarComponent: (
            <>
              {props.options?.extraToolbarComponent}
              {props.options?.export && props.onExportModifyCSV && (
                <CustomDownloadBtn
                  onNormalExport={onExportModifyCSV}
                  loading={props?.options?.loadingExport ?? props.isLoading}
                />
              )}
            </>
          )
        }}
      />
    </>
  )
}
